import * as React from 'react';
import { Dialog as SidePanelPrimitive } from '@base-ui/react/dialog';

import {
  SIDE_BODY,
  SIDE_CLOSE,
  SIDE_DESCRIPTION,
  SIDE_FOOTER,
  SIDE_HEADER,
  SIDE_SAFE_AREA,
  SIDE_SIZE,
  SIDE_TITLE,
  type SideSize,
} from '../lib/side-surface';
import { cn } from '../lib/utils';
import { ModalRoot, useForceVeil } from '../lib/modal-veil';
import { Button } from './button';
import { XIcon } from '@phosphor-icons/react';

/**
 * **O painel que flutua sobre a página, entrando pela direita** — o documento que se consulta sem
 * sair do contexto: voucher, quartos, a mala da viagem, notificações. `Dialog` do Base UI, como o
 * `Sheet`; os dois dividem a régua e a calha (`lib/side-surface.ts`) e diferem na superfície:
 *
 *   — `Sheet`: COLUNA encostada na borda — traço de 1px, canto reto, página atrás borrada.
 *   — `SidePanel`: APARTE flutuando — a separação é ELEVAÇÃO (`shadow-lg`, sem traço), o canto
 *     que chega na página arredonda (`rounded-l-2xl`), e o véu escurece sem desfocar: a página
 *     segue legível ao lado. Abaixo de `sm` é a tela inteira e o raio zera — raio em borda que
 *     encosta na janela desenha um canto de nada.
 */
function SidePanel({ ...props }: Readonly<SidePanelPrimitive.Root.Props>) {
  return (
    <ModalRoot.Provider value={props.modal ?? true}>
      <SidePanelPrimitive.Root data-slot="side-panel" {...props} />
    </ModalRoot.Provider>
  );
}

function SidePanelTrigger({ ...props }: Readonly<SidePanelPrimitive.Trigger.Props>) {
  return <SidePanelPrimitive.Trigger data-slot="side-panel-trigger" {...props} />;
}

function SidePanelClose({ ...props }: Readonly<SidePanelPrimitive.Close.Props>) {
  return <SidePanelPrimitive.Close data-slot="side-panel-close" {...props} />;
}

function SidePanelOverlay({
  className,
  forceRender,
  ...props
}: Readonly<SidePanelPrimitive.Backdrop.Props>) {
  const force = useForceVeil();
  return (
    <SidePanelPrimitive.Backdrop
      data-slot="side-panel-overlay"
      className={cn(
        // `genui2`: o véu sai por Portal e sem a classe nem `--z-popup` nem `--sand-900` existem.
        // Ink a 20% como o véu do `Dialog`, mas SEM blur — ver o docblock do `SidePanel`.
        'genui2',
        'fixed inset-0 z-(--z-popup) bg-(--sand-900)/20 transition-opacity duration-200 motion-reduce:transition-none data-ending-style:opacity-0 data-starting-style:opacity-0',
        className,
      )}
      // VOLL: veil by default even nested — see `DialogOverlay`.
      forceRender={forceRender ?? force}
      {...props}
    />
  );
}

function SidePanelContent({
  className,
  children,
  size = 'narrow',
  showCloseButton = true,
  closeLabel = 'Close',
  ...props
}: SidePanelPrimitive.Popup.Props & {
  /** A régua das superfícies laterais: tela inteira abaixo de `sm`, e a partir dele 448/576/768. */
  size?: SideSize;
  showCloseButton?: boolean;
  /** Nome acessível do botão de fechar; consumidores de superfície de cliente passam a copy
   *  traduzida. */
  closeLabel?: string;
}) {
  return (
    <SidePanelPrimitive.Portal data-slot="side-panel-portal">
      <SidePanelOverlay />
      <SidePanelPrimitive.Popup
        data-slot="side-panel-content"
        data-size={size}
        className={cn(
          'genui2',
          // `overflow-hidden` recorta o miolo no raio; quem rola é o `SidePanelBody`.
          'fixed inset-y-0 right-0 z-(--z-popup) flex h-full flex-col gap-4 overflow-hidden bg-popover text-sm text-popover-foreground shadow-lg outline-none transition duration-200 ease-in-out motion-reduce:transition-none data-ending-style:translate-x-[2.5rem] data-ending-style:opacity-0 data-starting-style:translate-x-[2.5rem] data-starting-style:opacity-0 sm:rounded-l-2xl',
          SIDE_SIZE[size],
          className,
          // O recorte do aparelho é invariante: depois do `className`, que não o apaga no `cn`.
          SIDE_SAFE_AREA,
          // As laterais: o painel encosta SEMPRE na direita (o recorte em paisagem); a esquerda só
          // abaixo de `sm`, onde ele é a tela inteira — o `env()` é da viewport, e na largura de
          // 448 colada à direita o recorte da esquerda está do outro lado da tela.
          'pr-[env(safe-area-inset-right,0px)] max-sm:pl-[env(safe-area-inset-left,0px)]',
        )}
        {...props}
      >
        {children}
        {showCloseButton && (
          <SidePanelPrimitive.Close
            data-slot="side-panel-close"
            render={<Button variant="ghost" className={SIDE_CLOSE} size="icon-sm" />}
          >
            <XIcon />
            <span className="sr-only">{closeLabel}</span>
          </SidePanelPrimitive.Close>
        )}
      </SidePanelPrimitive.Popup>
    </SidePanelPrimitive.Portal>
  );
}

function SidePanelHeader({ className, ...props }: React.ComponentProps<'div'>) {
  return <div data-slot="side-panel-header" className={cn(SIDE_HEADER, className)} {...props} />;
}

/** O miolo que rola, na calha do painel — publica `--page-gutter` para um `page-bleed` dentro dele. */
function SidePanelBody({ className, ...props }: React.ComponentProps<'div'>) {
  return <div data-slot="side-panel-body" className={cn(SIDE_BODY, className)} {...props} />;
}

function SidePanelFooter({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="side-panel-footer"
      data-side-footer=""
      className={cn(SIDE_FOOTER, className)}
      {...props}
    />
  );
}

function SidePanelTitle({ className, ...props }: Readonly<SidePanelPrimitive.Title.Props>) {
  return (
    <SidePanelPrimitive.Title
      data-slot="side-panel-title"
      className={cn(SIDE_TITLE, className)}
      {...props}
    />
  );
}

function SidePanelDescription({
  className,
  ...props
}: Readonly<SidePanelPrimitive.Description.Props>) {
  return (
    <SidePanelPrimitive.Description
      data-slot="side-panel-description"
      className={cn(SIDE_DESCRIPTION, className)}
      {...props}
    />
  );
}

export {
  SidePanel,
  SidePanelTrigger,
  SidePanelClose,
  SidePanelContent,
  SidePanelHeader,
  SidePanelBody,
  SidePanelFooter,
  SidePanelTitle,
  SidePanelDescription,
};
/* A régua mora em `lib/`, fora do mapa de exports: quem escolhe a largura pela tela a importa daqui. */
export type { SideSize };
